/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { FC, useCallback, useMemo, useRef, useState } from "react";

import { Actionable, Button, Card, Divider, Icon, Stack } from "@bookingcom/bui-react";

import BaseFareAttributes from "./BaseFareAttributes";
import BaseFareName from "./BaseFareName";
import FlightCardPrice from "./FlightCardPrice";
import FlightCardSegmentMobile from "./FlightCardSegment.mobile";
import { I18nChildContext, t, useI18n } from "@bookingcom/lingojs-react";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../../store";
import { FlightCardBoundProps } from "./FlightCardBound.desktop";
import { ArrowNavDownIcon, ArrowNavUpIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import useIsInViewport from "../../../../hooks/useIsInViewport";
import FareSelector from "../../../../branded-fares/components/FareSelector/components/FareSelector";
import useFetchBrandedFares from "../../../../branded-fares/hooks/useFetchBrandedFares";
import { FlightCardHighlight, FlightCardHighlightStyle } from "./FlightCardHighlight";
import { FlightCardBadges } from "./FlightCardBadges";
import { FlightCardBadge } from "./FlightCardBadge";
import Frame from "../../Frame";
import styles from "./FlightCardBound.module.css";
import { getCampaignBadgesConfig, getCampaignBestOfferHighlightConfig } from "../../../../utils/flightCampaignConfig";
import { RebookCostBreakdown } from "../../RebookFlow/RebookCostBreakdown/RebookCostBreakdown";
import { FlightBadgeVariant, UIRebookFlow } from "@flights/types";
import { mcn } from "../../../../utils/mergeClassnames";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import { carrierUtils, flightsSessionStore } from "@flights/web-api-utils-universal";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import { useSelector } from "react-redux";
import { getSearchDisplayOptions } from "../../../../store/searchResults/selectors";
import { CardCarriersCombined } from "./elements/CardCarriersCombined/CardCarriersCombined";
import { FlightCardLuggageMobile } from "./elements/FlightCardLuggage/FlightCardLuggage.mobile";
import FlightCardPriceOutbound from "./FlightCardPrice.outbound";
import { useLocation } from "react-router-dom";
import { useSplitBoundsCondition } from "../../../../utils/experiments/funnel/flights_split_bounds_us";
import { trackWithDefaultStage } from "../../../../utils/et-microconversion";
import { isOfMetaOrigin } from "../../../../utils/marketing-url-params";
import flights_merch_toolkit_offer_pinning_v2 from "../../../../utils/experiments/genius/flights_merch_toolkit_offer_pinning_v2";
import flights_lve_merch_toolkit_offer_highlighting from "../../../../utils/experiments/genius/flights_lve_merch_toolkit_offer_highlighting";

type Props = FlightCardBoundProps & {
  /* start - flights_split_bounds_us */
  boundsVariant?: "outbound" | "inbound";
  /* end - flights_split_bounds_us */
};

const FlightCardBound: FC<Props> = (props) => {
  const {
    flight,
    onClick,
    pricePerPerson,
    rebookProps,
    forceHideLastAvailableSeats = false,
    isAlternativeFareCard = false,
    index,
    /* start - flights_split_bounds_us */
    boundsVariant,
    /* end - flights_split_bounds_us */
    /* start - flights_alternative_fares_for_price_change_and_sold_out_errors_web */
    viewFlightDetailsCtaLabel,
    disabled = false
    /* end - flights_alternative_fares_for_price_change_and_sold_out_errors_web */
  } = props;
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const { searchCriteria } = useStore();
  const { adults, children } = searchCriteria;
  const i18n = useI18n() as I18nChildContext;
  const { isInternal } = useGlobalContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rebookFlow = queryParams.get("rebookFlow") as UIRebookFlow | null;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const displayOptions = useSelector(getSearchDisplayOptions);
  const hasBrandedFares = !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
  const hasPrefetchedBrandedFareOffers = !flight.requestableBrandedFares && hasBrandedFares;
  const showFareSelectorDefault =
    (flight.requestableBrandedFares || hasBrandedFares) && !!displayOptions?.brandedFaresShownByDefault;
  const [showFareSelector, toggleShowFareSelector] = useState(showFareSelectorDefault);
  const flightCardBoundRef = useRef<HTMLButtonElement>(null);
  const isInViewport = useIsInViewport(flightCardBoundRef.current);

  /* start - flights_web_sr_redesign_mdot_v2 */
  const canShowFareSelector =
    boundsVariant !== "outbound" && (flight.requestableBrandedFares || hasPrefetchedBrandedFareOffers);
  /* end - flights_web_sr_redesign_mdot_v2 */

  const shouldPinMetaOfferOnSrVariant =
    !!trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_mdot", 1) &&
    isOfMetaOrigin() &&
    flightsSessionStore.get("landingOfferKey") === flight.offerKeyToHighlight;

  const isGeniusHighlightedOffer =
    flight.highlightInfo?.highlight && flight.highlightInfo.reason === "GENIUS_AIRLINE_HIGHLIGHTED_OFFER";
  const isGeniusUprankedOffer =
    flight.highlightInfo?.highlight &&
    (flight.highlightInfo.reason === "GENIUS_AIRLINE_FARE_OFFER" ||
      flight.highlightInfo.reason === "GENIUS_AIRLINE_UPRANKED_OFFER");
  const isCrossSellOffer = flight.highlightInfo?.highlight && flight.highlightInfo.reason === "CROSS_SELL_OFFER";

  const shouldPinOfferOnSr = isCrossSellOffer || shouldPinMetaOfferOnSrVariant;

  const getPinnedHighlightText = () => {
    if (shouldPinMetaOfferOnSrVariant) return i18n.trans(t("flights_sr_pin_meta_banner_selected"));
    if (isGeniusHighlightedOffer || isGeniusUprankedOffer) return flight.highlightInfo?.message ?? "";
    return flight.highlightInfo?.message || i18n.trans(t("flights_meta_sr_pinned_flight_label"));
  };
  const pinnedHighlightText = getPinnedHighlightText();

  const pinnedInfoTooltip =
    isGeniusUprankedOffer && flight.highlightInfo?.infoMessage
      ? {
          content: flight.highlightInfo.infoMessage.subtitle,
          ariaLabel: i18n.trans(t("a11y_flights_sr_featured_flight_more_informatio_popup")),
          title: flight.highlightInfo.infoMessage.title
        }
      : undefined;

  const pinnedHighlightStyle = isGeniusHighlightedOffer
    ? FlightCardHighlightStyle.NEUTRAL
    : FlightCardHighlightStyle.PRIMARY;

  const { bestOfferHighlightStyle, bestOfferHighlightText } = getCampaignBestOfferHighlightConfig(
    flight.campaignDisplay?.bestOfferHighlight
  );
  const shouldShowBestOfferHightlight = !!(bestOfferHighlightStyle && bestOfferHighlightText);

  const campaignBadges = getCampaignBadgesConfig(flight.campaignDisplay?.badges);

  const carriers = carrierUtils.getMarketingCarriers(flight.segments);

  useFetchBrandedFares(
    flight.token,
    isInViewport,
    boundsVariant !== "outbound" && !isAlternativeFareCard && !!flight.requestableBrandedFares,
    true,
    !!flight.brandedFareInfo?.fareName,
    carriers
  );

  const hasFlexibleTicketBadge = boundsVariant !== "outbound" && flight.ancillaries?.flexibleTicket;

  const { isSplitBounds } = useSplitBoundsCondition();

  const handleOnClick = useCallback(() => {
    if (!breakdownOpen) {
      onClick(flight);
    }
  }, [breakdownOpen, flight, onClick]);

  const showFlightCardBadges =
    flight.badges?.length || campaignBadges.length > 0 || hasFlexibleTicketBadge || flight.hasAppleCardBenefits;

  const highlightFireflySR =
    isInternal &&
    flight.token.startsWith("c8e49_") &&
    new URLSearchParams(window.location.search).get("highlight_firefly") === "1";

  const canShowFareSelectorButton =
    boundsVariant !== "outbound" &&
    ((flight.requestableBrandedFares && !isAlternativeFareCard) ||
      (!flight.requestableBrandedFares && hasPrefetchedBrandedFareOffers));

  const toggleFareSelectorIcon = useMemo(
    () => <Icon svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon} attributes={{ "aria-hidden": true }} />,
    [showFareSelector]
  );
  const viewDetailsButton = (
    <Button
      ref={buttonRef}
      attributes={{
        "aria-label": `${
          viewFlightDetailsCtaLabel ||
          (boundsVariant === "outbound"
            ? i18n.trans(t("flights_sr_split_bounds_us_cta_select"))
            : i18n.trans(t("flights_apex_bf_sr_cta1_view_details")))
        } ${hasFlexibleTicketBadge ? i18n.trans(t("flights_apex_sr_flex_badge")) : ""}`,
        "aria-describedby": `flight-card-${index}`
      }}
      text={
        viewFlightDetailsCtaLabel ||
        (boundsVariant === "outbound"
          ? i18n.trans(t("flights_sr_split_bounds_us_cta_select"))
          : i18n.trans(t("flights_apex_bf_sr_cta1_view_details")))
      }
      onClick={() => {
        if (flight.highlightInfo) {
          if (isGeniusUprankedOffer)
            flights_merch_toolkit_offer_pinning_v2.trackFeaturedOfferClick(flight.highlightInfo);
          if (isGeniusHighlightedOffer) flights_lve_merch_toolkit_offer_highlighting.goals.featured_offer_click();
        }
        onClick(flight);
      }}
      variant="secondary"
      className={styles.button}
    />
  );

  return (
    <Card
      fill
      className={mcn(styles.card, highlightFireflySR && styles.highlightFirefly)}
      attributes={{ ref: flightCardBoundRef, id: `flight-card-${index}` }}
      variant={shouldPinOfferOnSr ? "elevated" : "neutral"}
    >
      {isGeniusUprankedOffer || isGeniusHighlightedOffer ? (
        <FlightCardHighlight
          style={pinnedHighlightStyle}
          text={pinnedHighlightText}
          showGeniusLogo
          infoTooltip={pinnedInfoTooltip}
        />
      ) : null}

      <Actionable
        disabled={disabled}
        ref={buttonRef}
        onClick={handleOnClick}
        className={mcn(styles.button, styles.outboundButtonCard)}
      >
        {shouldPinOfferOnSr ? (
          <FlightCardHighlight style={FlightCardHighlightStyle.PRIMARY} text={pinnedHighlightText} />
        ) : shouldShowBestOfferHightlight ? (
          <FlightCardHighlight style={bestOfferHighlightStyle} text={bestOfferHighlightText} textEmphasized={true} />
        ) : null}

        <Frame pt={4} pr={4} pb={2} pl={4} direction="column" justifyContent="space-between">
          {showFlightCardBadges && (
            <FlightCardBadges className={styles.badges}>
              {flight?.badges?.map(({ text, variant, type }, idx) => (
                <FlightCardBadge
                  key={text + idx}
                  text={text}
                  style={variant}
                  alternative
                  type={type}
                  flightToken={flight.token}
                />
              ))}

              {!isSplitBounds
                ? hasFlexibleTicketBadge && (
                    <FlightCardBadge
                      style={FlightBadgeVariant.CONSTRUCTIVE}
                      alternative={true}
                      text={i18n.trans(t("flights_apex_sr_flex_badge"))}
                      flightToken={flight.token}
                    />
                  )
                : null}

              {flight.hasAppleCardBenefits && (
                <FlightCardBadge
                  style={FlightBadgeVariant.CALLOUT}
                  alternative={true}
                  text={i18n.trans(t("apple_rewards_flights_sr_badge_title"))}
                  flightToken={flight.token}
                />
              )}
            </FlightCardBadges>
          )}

          <BaseFareAttributes flight={flight} />
          <Frame
            attributes={{
              style: {
                width: "100%"
              }
            }}
            justifyContent="space-around"
          >
            {flight.segments.map((segment, segmentIndex) => {
              return (
                <Frame key={segmentIndex} className={styles.segment}>
                  <FlightCardSegmentMobile segment={segment} idx={segmentIndex} />
                </Frame>
              );
            })}
            <Frame>
              <CardCarriersCombined segments={flight.segments} />
            </Frame>
          </Frame>
          <Divider className={styles.divider} />

          {campaignBadges.length > 0 && !isSplitBounds && (
            <FlightCardBadges className={mcn(styles.badges, styles.badgesInBottomSection)}>
              {campaignBadges.map((badge, idx) => (
                <FlightCardBadge
                  key={`keyCampaignBadge-${idx}`}
                  alternative={false}
                  isInteractive={false}
                  {...badge}
                  flightToken={flight.token}
                />
              ))}
            </FlightCardBadges>
          )}

          <Frame direction="row" justifyContent="space-between" alignItems="center" className={styles.priceBlock}>
            {boundsVariant === "outbound" ? null : (
              <Frame direction="row" alignItems="center" justifyContent="flex-start" className={styles.luggage}>
                <FlightCardLuggageMobile
                  flight={flight}
                  brandedFareInfo={canShowFareSelector ? flight.brandedFareInfo : undefined}
                  align="start"
                />
              </Frame>
            )}

            <Frame className={styles.price}>
              <BaseFareName flight={flight} />
              {rebookProps ? (
                <RebookCostBreakdown
                  {...rebookProps}
                  breakdownOpen={breakdownOpen}
                  setBreakdownOpen={setBreakdownOpen}
                  rebookFlow={rebookFlow}
                />
              ) : boundsVariant === "outbound" ? (
                <FlightCardPriceOutbound flight={flight} />
              ) : (
                <FlightCardPrice
                  flight={flight}
                  breakdownOpen={breakdownOpen}
                  setBreakdownOpen={setBreakdownOpen}
                  showPricePerPerson={!!boundsVariant || (pricePerPerson && adults + children.length > 1)}
                  showAvailableSeats={!forceHideLastAvailableSeats}
                  popoverCloseAriaLabel={i18n.trans(t("flights_a11y_price_breakdown_close_sr"))}
                  boundsVariant={boundsVariant}
                />
              )}

              {campaignBadges.length > 0 && isSplitBounds && (
                <Stack gap={1} direction="row" justifyContent="end">
                  {campaignBadges.map((badge, idx) => (
                    <FlightCardBadge
                      key={`keyCampaignBadge-${idx}`}
                      alternative={false}
                      isInteractive={false}
                      {...badge}
                      flightToken={flight.token}
                    />
                  ))}
                </Stack>
              )}
            </Frame>
          </Frame>
        </Frame>
      </Actionable>

      <Frame className={styles.buttons}>
        <Stack gap={3}>
          {isSplitBounds && canShowFareSelectorButton ? (
            <Stack direction="row" grow>
              <Stack.Item alignSelf="end">
                <Button
                  attributes={{
                    "aria-expanded": !!showFareSelector,
                    "aria-controls": `flights-fare-selector-${index}`,
                    "aria-describedby": `flight-card-${index}`,
                    "aria-label": showFareSelector
                      ? i18n.trans(t("a11y_flights_apex_bf_sr_hide_options"))
                      : i18n.trans(t("a11y_flights_apex_bf_sr_expand_options"))
                  }}
                  icon={toggleFareSelectorIcon}
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleShowFareSelector(!showFareSelector);
                    flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
                  }}
                />
              </Stack.Item>
              <Stack.Item grow>{viewDetailsButton}</Stack.Item>
            </Stack>
          ) : (
            <>
              <Button
                ref={buttonRef}
                attributes={{
                  "aria-label":
                    viewFlightDetailsCtaLabel ||
                    `${
                      boundsVariant === "outbound"
                        ? i18n.trans(t("flights_sr_split_bounds_us_cta_select"))
                        : i18n.trans(t("flights_apex_bf_sr_cta1_view_details"))
                    } ${hasFlexibleTicketBadge ? i18n.trans(t("flights_apex_sr_flex_badge")) : ""}`,
                  "aria-describedby": `flight-card-${index}`
                }}
                text={
                  viewFlightDetailsCtaLabel || boundsVariant === "outbound"
                    ? i18n.trans(t("flights_sr_split_bounds_us_cta_select"))
                    : i18n.trans(t("flights_apex_bf_sr_cta1_view_details"))
                }
                onClick={() => {
                  onClick(flight);
                }}
                variant="secondary"
                className={styles.button}
                disabled={disabled}
              />
              {boundsVariant !== "outbound" ? (
                (flight.requestableBrandedFares && !isAlternativeFareCard) ||
                (!flight.requestableBrandedFares && hasPrefetchedBrandedFareOffers) ? (
                  <Button
                    attributes={{
                      "aria-expanded": showFareSelector,
                      "aria-controls": `flights-fare-selector-${index}`,
                      "aria-describedby": `flight-card-${index}`
                    }}
                    text={
                      showFareSelector
                        ? i18n.trans(t("flights_apex_sr_flex_cta_hide"))
                        : i18n.trans(t("flights_apex_sr_flex_cta_explore"))
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleShowFareSelector(!showFareSelector);
                      flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
                    }}
                    variant="tertiary"
                    iconPosition="end"
                    icon={
                      <Icon
                        svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon}
                        attributes={{ "aria-hidden": true }}
                      />
                    }
                    className={styles.button}
                  />
                ) : null
              ) : null}
            </>
          )}
        </Stack>
      </Frame>

      {canShowFareSelector && (
        <div id={`flights-fare-selector-${index}`}>{showFareSelector && <FareSelector flight={flight} />}</div>
      )}
    </Card>
  );
};

const FlightCard = React.memo(FlightCardBound);

export default FlightCard;
